import React, { useState, useRef, useLayoutEffect } from 'react';
import { useTheme } from '../ThemeContext';
import StarRating from './StarRating';

// Home page redesign (Claude Design handoff "Einstein Home layout 1a") — the five pillar
// bubbles rearranged onto a flattened, wide arc across the upper half of the page, replacing
// PillarBubbleSpider's honeycomb layout ON HOME ONLY. PillarBubbleSpider itself is untouched —
// it's still IndividualReport.js's shared template (see its own header comment), and this is a
// deliberately separate component so that page doesn't inherit a layout change never asked for.
//
// Positions are the design's literal pixel table (not a live ellipse formula) — a true evenly-
// angle-spaced ellipse through the same 5 points doesn't reproduce the handoff's exact bubble
// centres (checked: the ellipse a single cx/cy/rx/ry pair implies puts Identity/Technical off
// by ~40-60px from the spec), so this hardcodes the table directly, same convention
// PillarBubbleSpider.js already uses for its own PILLAR_LAYOUT.
const ARC_WIDTH = 1440;
const ARC_HEIGHT = 416;
const ARC_LAYOUT = {
  availability: { label: 'Availability', cx: 250, cy: 320, size: 128 },
  identity:     { label: 'Identity',     cx: 445, cy: 160, size: 128 },
  physical:     { label: 'Physical',     cx: 720, cy: 92,  size: 148 },
  technical:    { label: 'Technical',    cx: 995, cy: 160, size: 128 },
  tactical:     { label: 'Tactical',     cx: 1190, cy: 320, size: 128 },
};
const PILLAR_ORDER = ['availability', 'identity', 'physical', 'technical', 'tactical'];

const PERIODS = [
  { value: 'week', label: 'Week' },
  { value: '28day', label: '28-Day' },
  { value: 'preseason', label: 'Pre-season' },
  { value: 'season', label: 'Season' },
];

// Same technique as PillarBubbleSpider's useHoneycombScale (proven, don't reinvent) — including
// budgeting remaining viewport height via `bottomReserve`, since the console below this arc
// needs to fit on screen too now (2026-09-03 fix — the first version only scaled by width and
// ignored height, so on a short viewport the console got pushed below the fold).
function useArcScale(bottomReserve) {
  const stageRef = useRef(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = stageRef.current;
    const parent = el?.parentElement;
    if (!el || !parent) return;
    const recompute = () => {
      const availWidth = parent.clientWidth;
      const top = el.getBoundingClientRect().top;
      const availHeight = window.innerHeight - top - bottomReserve;
      const next = Math.min(1, availWidth / ARC_WIDTH, Math.max(180, availHeight) / ARC_HEIGHT);
      setScale(next > 0 ? next : 1);
    };
    recompute();
    window.addEventListener('resize', recompute);
    const ro = new ResizeObserver(recompute);
    ro.observe(parent);
    return () => { window.removeEventListener('resize', recompute); ro.disconnect(); };
  }, [bottomReserve]);
  return { stageRef, scale };
}

function ArcBubble({ pillarKey, value, isDay, accent, onClick }) {
  const { label, cx, cy, size } = ARC_LAYOUT[pillarKey];
  const hasData = value != null;
  const pct = hasData ? Math.max(0, Math.min(100, value)) : 0;
  const track = isDay ? '#ccd6dd' : 'rgba(255,255,255,0.14)';
  const ring = hasData
    ? `conic-gradient(from 180deg, ${accent} 0 ${pct}%, ${track} ${pct}% 100%)`
    : track;
  const clickable = typeof onClick === 'function';

  return (
    <button
      type="button"
      onClick={clickable ? onClick : undefined}
      title={clickable ? `Go to ${label}` : label}
      style={{
        position: 'absolute', left: cx, top: cy, transform: 'translate(-50%,-50%)',
        width: size, height: size, padding: 7, borderRadius: '999px', background: ring,
        border: 'none',
        boxShadow: isDay ? '0 10px 24px rgba(24,40,56,.13)' : '0 10px 24px rgba(0,0,0,.35)',
        cursor: clickable ? 'pointer' : 'default', boxSizing: 'border-box',
      }}
    >
      <div style={{
        width: '100%', height: '100%', borderRadius: '999px',
        background: isDay ? '#fbfcfd' : '#0e1216',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
      }}>
        <span style={{ fontSize: size >= 140 ? 11 : 10, fontWeight: 600, letterSpacing: '.12em', textTransform: 'uppercase', color: isDay ? 'rgba(20,24,28,0.5)' : 'rgba(255,255,255,0.5)' }}>
          {label}
        </span>
        <span style={{ fontSize: size >= 140 ? 46 : 40, fontWeight: 800, color: hasData ? (isDay ? '#1c2024' : '#fff') : (isDay ? 'rgba(20,24,28,0.3)' : 'rgba(255,255,255,0.3)') }}>
          {hasData ? Math.round(value) : '—'}
        </span>
      </div>
    </button>
  );
}

export default function EinsteinArc({
  crestImageUrl, title, subtitle, stars, pillars, period, onPeriodChange,
  bubbleLinks = {}, bottomReserve = 24,
}) {
  const { theme } = useTheme();
  const isDay = theme === 'day';
  const accent = isDay ? '#0891b2' : '#22d3ee';
  const { stageRef, scale } = useArcScale(bottomReserve);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 6 }}>
        <div style={{ display: 'inline-flex', gap: 4, padding: 5, borderRadius: 999, background: isDay ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.06)', boxShadow: `inset 0 0 0 1px ${isDay ? 'rgba(24,40,56,.05)' : 'rgba(255,255,255,0.10)'}` }}>
          {PERIODS.map(p => {
            const active = period === p.value;
            return (
              <button key={p.value} type="button" onClick={() => onPeriodChange(p.value)}
                style={{
                  padding: '7px 22px', borderRadius: 999, fontSize: 14, fontWeight: active ? 600 : 400,
                  border: 'none', cursor: 'pointer',
                  color: active ? '#fff' : (isDay ? '#6b7683' : 'rgba(255,255,255,.65)'),
                  background: active ? accent : 'transparent',
                }}>
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Outer wrapper is sized to the ACTUAL rendered (scaled) dimensions, so margin:auto can
          correctly centre it — a box wider than its parent can't be centered via margin:auto
          (the browser clamps auto margins to 0 and it silently left-aligns instead, which was
          the exact cause of the "bubbles are way to the left" bug: the old version centered the
          unscaled 1440px-wide box, not the scaled one). The inner content div is `position:
          absolute` off document flow at the wrapper's top-left, at its native 1440x416 size,
          scaled with `transformOrigin: top left` — since the wrapper is already the correct
          scaled size, the scaled content exactly fills it. Same proven pattern as
          PillarBubbleSpider's useHoneycombScale. */}
      <div ref={stageRef} style={{ width: ARC_WIDTH * scale, height: ARC_HEIGHT * scale, position: 'relative', margin: '0 auto' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, width: ARC_WIDTH, height: ARC_HEIGHT, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
          {/* decorative arc guide, behind the bubbles */}
          <div style={{
            position: 'absolute', left: '50%', top: 92, transform: 'translateX(-50%)',
            width: 940, height: 228,
            border: `1.5px dashed ${isDay ? 'rgba(8,145,178,.28)' : 'rgba(34,211,238,.22)'}`,
            borderBottom: 'none', borderRadius: '470px 470px 0 0 / 228px 228px 0 0',
          }} />

          {PILLAR_ORDER.map(k => (
            <ArcBubble key={k} pillarKey={k} value={pillars[k]} isDay={isDay} accent={accent}
              onClick={bubbleLinks[k]} />
          ))}

          {/* crest + identity group, nested inside the arc */}
          <div style={{ position: 'absolute', left: '50%', top: 210, transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <img src={crestImageUrl} alt="" style={{ width: 96, height: 'auto', filter: `drop-shadow(0 12px 26px rgba(0,0,0,${isDay ? 0.18 : 0.4}))` }} />
            <span style={{ fontFamily: "'Archivo Black','Arial Black',Impact,sans-serif", fontSize: 30, letterSpacing: '.01em', color: isDay ? '#1c2024' : '#fff', whiteSpace: 'nowrap' }}>
              {title}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: -4 }}>
              {stars == null ? (
                <span style={{ fontSize: 12, color: isDay ? 'rgba(20,24,28,0.4)' : 'rgba(255,255,255,0.4)' }}>No rating yet</span>
              ) : (
                <>
                  <span style={{ fontWeight: 700, fontSize: 18, color: accent }}>{stars.toFixed(1)}</span>
                  <StarRating value={stars} size={15} color={accent} emptyColor={isDay ? 'rgba(20,24,28,0.15)' : 'rgba(255,255,255,0.2)'} gap={2} showNumber={false} />
                  <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.14em', color: isDay ? 'rgba(20,24,28,0.4)' : 'rgba(255,255,255,0.4)' }}>{subtitle}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
